import { Toast } from "vant"

/**
 * 禁止输入框复制和粘贴（包括输入法自带的粘贴功能）
 * @param {string|NodeList|HTMLElement} selector - 选择器字符串、NodeList 或单个元素
 * @param {Object} options - 配置选项
 * @param {Function} options.onPaste - 粘贴拦截时的回调函数
 * @param {Function} options.onCopy - 复制拦截时的回调函数
 * @param {string} options.pasteMessage - 粘贴拦截提示信息
 * @param {string} options.copyMessage - 复制拦截提示信息
 */
export function preventCopyPaste(selector, options = {}) {
  const { onPaste, onCopy, pasteMessage = "禁止粘贴", copyMessage = "禁止复制" } = options

  // 获取元素列表
  let elements = []
  if (typeof selector === "string") {
    elements = Array.from(document.querySelectorAll(selector))
  } else if (selector instanceof NodeList) {
    elements = Array.from(selector)
  } else if (selector instanceof HTMLElement) {
    elements = [selector]
  } else if (Array.isArray(selector)) {
    elements = selector
  }

  if (elements.length === 0) {
    console.warn("preventCopyPaste: 未找到匹配的元素")
    return
  }

  elements.forEach((edit) => {
    if (!edit || !(edit instanceof HTMLElement)) {
      return
    }

    // 检查是否已经绑定过，避免重复绑定
    if (edit.dataset.preventPasteBound === "true") {
      return
    }
    edit.dataset.preventPasteBound = "true"

    // 记录上一次的值和输入状态
    let lastValue = edit.value || ""
    let isComposing = false // 是否正在输入法输入
    let lastInputTime = Date.now() // 上次输入时间

    // 禁止复制
    edit.oncopy = function (e) {
      e.preventDefault()
      e.stopPropagation()
      Toast(copyMessage)
      if (onCopy && typeof onCopy === "function") {
        onCopy(e, edit)
      }
      return false
    }

    // 标准粘贴事件
    edit.onpaste = function (e) {
      e.preventDefault()
      e.stopPropagation()
      Toast(pasteMessage)
      if (onPaste && typeof onPaste === "function") {
        onPaste(e, edit)
      }
      return false
    }

    // 监听输入法输入开始
    edit.addEventListener("compositionstart", () => {
      isComposing = true
    })

    // 监听输入法输入更新
    edit.addEventListener("compositionupdate", () => {
      isComposing = true
    })

    // 监听输入法输入结束
    edit.addEventListener("compositionend", () => {
      isComposing = false
    })

    // 监听 beforeinput 事件（现代浏览器支持，可以拦截输入法粘贴）
    edit.addEventListener("beforeinput", (e) => {
      // 如果是插入文本类型，且不是输入法输入状态
      if (
        e.inputType === "insertText" ||
        e.inputType === "insertFromPaste" ||
        e.inputType === "insertFromDrop" ||
        e.inputType === "insertCompositionText"
      ) {
        // 检查是否是一次性输入大量文本（可能是粘贴）
        const inputData = e.data || ""
        if (inputData && inputData.length > 1 && !isComposing) {
          e.preventDefault()
          e.stopPropagation()
          Toast(pasteMessage)
          if (onPaste && typeof onPaste === "function") {
            onPaste(e, edit)
          }
          return false
        }
      }
      // 直接拦截粘贴类型
      if (e.inputType === "insertFromPaste" || e.inputType === "insertFromDrop") {
        e.preventDefault()
        e.stopPropagation()
        Toast(pasteMessage)
        if (onPaste && typeof onPaste === "function") {
          onPaste(e, edit)
        }
        return false
      }
    })

    // 监听 input 事件，检测异常输入（兜底方案）
    edit.addEventListener("input", (e) => {
      const currentValue = e.target.value || ""
      const valueChange = currentValue.length - lastValue.length
      const currentTime = Date.now()
      const timeDiff = currentTime - lastInputTime

      // 如果不是输入法输入状态，且一次性输入超过1个字符，可能是粘贴
      // 或者输入时间间隔很短（小于50ms）且输入字符数较多，也可能是粘贴
      if (!isComposing && valueChange > 1 && timeDiff < 100) {
        // 恢复上一次的值
        e.target.value = lastValue
        // 触发 input 事件以同步 v-model
        const inputEvent = new Event("input", { bubbles: true })
        e.target.dispatchEvent(inputEvent)
        Toast(pasteMessage)
        if (onPaste && typeof onPaste === "function") {
          onPaste(e, edit)
        }
        return false
      }

      // 更新记录的值和时间
      lastValue = currentValue
      lastInputTime = currentTime
    })

    // 监听 drop 事件（拖拽粘贴）
    edit.addEventListener("drop", (e) => {
      e.preventDefault()
      e.stopPropagation()
      Toast(pasteMessage)
      if (onPaste && typeof onPaste === "function") {
        onPaste(e, edit)
      }
      return false
    })

    // 监听 dragenter 和 dragover 事件，禁止拖拽
    edit.addEventListener("dragenter", (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    edit.addEventListener("dragover", (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    // 监听 contextmenu 事件，禁止右键菜单（可选）
    edit.addEventListener("contextmenu", (e) => {
      e.preventDefault()
      return false
    })
  })
}

/**
 * 移除输入框的复制粘贴限制
 * @param {string|NodeList|HTMLElement} selector - 选择器字符串、NodeList 或单个元素
 */
export function removePreventCopyPaste(selector) {
  // 获取元素列表
  let elements = []
  if (typeof selector === "string") {
    elements = Array.from(document.querySelectorAll(selector))
  } else if (selector instanceof NodeList) {
    elements = Array.from(selector)
  } else if (selector instanceof HTMLElement) {
    elements = [selector]
  } else if (Array.isArray(selector)) {
    elements = selector
  }

  elements.forEach((edit) => {
    if (!edit || !(edit instanceof HTMLElement)) {
      return
    }

    // 移除事件监听器（注意：这里只能移除通过 addEventListener 添加的监听器）
    // oncopy 和 onpaste 可以直接设置为 null
    edit.oncopy = null
    edit.onpaste = null
  })
}
